import React, {
  useState, useRef, useEffect, useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';

/** Module-level counter so each circle gets a unique tooltip id. */
let circleInstanceId = 0;

const NO_COLOR_BORDER = '#444444';

/** Treat empty string / white / unset as "no color selected". #444444 counts as
 * unset too: it is the NO_COLOR_BORDER constant and never a selectable palette
 * color, but a title-update bug persisted it onto some days. */
const hasColor = (color) => Boolean(color)
  && color.trim() !== ''
  && color.toLowerCase() !== '#ffffff'
  && color.toLowerCase() !== NO_COLOR_BORDER
  && color !== 'blank';

const Wrapper = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TriggerCircle = styled('button')`
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  margin-top: ${(props) => props.marginTop}px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  background: ${(props) => (props.filled ? props.swatch : 'white')};
  border: 1px solid ${(props) => (props.filled ? props.swatch : NO_COLOR_BORDER)};
  box-sizing: border-box;
`;

// Portaled to document.body (position: fixed) so it isn't clipped by the
// day header's `overflow: hidden` container. Anchored via inline top/left.
const Popover = styled('div')`
  position: fixed;
  transform: translateY(-50%);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 9999;
`;

const Swatch = styled('div')`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props) => props.swatch};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoColorSwatch = styled('div')`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #9E9E9E;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkoutColorCircle = ({
  colorOptions, currentColor, onSelect, size, marginTop,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const tooltipIdRef = useRef(`workout-color-${(circleInstanceId += 1)}`);
  const tooltipId = tooltipIdRef.current;

  // Close the popover on any outside click. The popover is portaled to the body,
  // so it must be excluded from the "outside" check alongside the trigger.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      const inTrigger = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopover = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inTrigger && !inPopover) {
        setIsOpen(false);
      }
    };
    // Close on scroll — the fixed popover would otherwise detach from the
    // trigger. Capture phase so it catches nested scroll containers (e.g. the
    // week-view horizontal scroller), whose scroll events don't bubble.
    const handleScroll = () => setIsOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Position the fixed popover once it's rendered so we can measure its width
  // and flip it to the left of the trigger when opening right would overflow the
  // viewport (e.g. week-view headers near the right edge of the screen).
  // useLayoutEffect runs before paint, so the corrected position is applied
  // without a visible flash.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !popoverRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const popover = popoverRef.current.getBoundingClientRect();
    const margin = 8;
    let left = trigger.right + margin;
    if (left + popover.width > window.innerWidth - margin) {
      // Not enough room on the right — open to the left of the trigger.
      left = trigger.left - popover.width - margin;
    }
    if (left < margin) left = margin; // never clip off the left edge either
    setPopoverPos({ top: trigger.top + trigger.height / 2, left });
  }, [isOpen]);

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleSwatchClick = (e, color) => {
    e.stopPropagation();
    onSelect(color);
    setIsOpen(false);
  };

  const handleSwatchKeyDown = (e, color) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSwatchClick(e, color);
    }
  };

  const filled = hasColor(currentColor);

  // Show the "no color" opt-out first (left), then the colors.
  const orderedOptions = [
    ...colorOptions.filter((option) => option.color === 'blank'),
    ...colorOptions.filter((option) => option.color !== 'blank'),
  ];

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerCircle
        ref={triggerRef}
        type='button'
        aria-label='Workout color'
        aria-haspopup='menu'
        aria-expanded={isOpen}
        data-tooltip-id={tooltipId}
        filled={filled}
        swatch={currentColor}
        size={size}
        marginTop={marginTop}
        onClick={handleTriggerClick}
      />
      {!isOpen && (
        <ReactTooltip
          id={tooltipId}
          place='top'
          content={(
            <Text color='white' fontSize='13px'>
              Workout Color
            </Text>
          )}
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: 'white',
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            zIndex: 9999,
          }}
        />
      )}
      {isOpen && ReactDOM.createPortal(
        <Popover
          ref={popoverRef}
          style={{ top: popoverPos.top, left: popoverPos.left }}
          // Portaled to document.body, so React-tree bubbling would carry this
          // mousedown to month view's grid handler (handleGridMouseDown), whose
          // day-cell check misses (target lives on body) and clears the active
          // day. Stop it here — click-only stopPropagation on the swatch is too
          // late, since the grid listens on mousedown.
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Render the "no color" opt-out first (left), then the colors. */}
          {orderedOptions.map((option) => (
            option.color === 'blank' ? (
              <NoColorSwatch
                key={option.key}
                role='button'
                tabIndex={0}
                aria-label='No color'
                onClick={(e) => handleSwatchClick(e, option.color)}
                onKeyDown={(e) => handleSwatchKeyDown(e, option.color)}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={14}
                  icon='opt-out'
                  color='#9E9E9E'
                />
              </NoColorSwatch>
            ) : (
              <Swatch
                key={option.key}
                swatch={option.color}
                role='button'
                tabIndex={0}
                aria-label={`Color ${option.key}`}
                onClick={(e) => handleSwatchClick(e, option.color)}
                onKeyDown={(e) => handleSwatchKeyDown(e, option.color)}
              />
            )
          ))}
        </Popover>,
        document.body,
      )}
    </Wrapper>
  );
};

WorkoutColorCircle.propTypes = {
  colorOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    color: PropTypes.string,
  })).isRequired,
  currentColor: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  size: PropTypes.number,
  marginTop: PropTypes.number,
};

WorkoutColorCircle.defaultProps = {
  currentColor: '',
  size: 13,
  marginTop: 0,
};

export default WorkoutColorCircle;
